import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';

import { tapHaptic } from '../../shared/lib/haptics';
import { Button } from '../../shared/ui/Button';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { Select } from '../../shared/ui/Select';
import { Skeleton } from '../../shared/ui/Skeleton';
import { StatusBanner } from '../../shared/ui/StatusBanner';
import { useToast } from '../../shared/ui/toast-context';

import {
  ADMIN_QUERY_KEYS,
  addMember,
  addStudent,
  getInstitute,
  listInstituteMembers,
  listInstituteStudents,
  provisionUser,
} from './api';
import s from './InstituteDetailPage.module.css';
import {
  addMemberSchema,
  addStudentSchema,
  EXAM_TYPES,
  INSTITUTE_ROLES,
  type AddMemberInput,
  type AddStudentInput,
} from './schemas';

function initialsFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '?';
  const parts = local.split(/[._-]/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase();
}

const SKELETON_ROWS = [0, 1, 2] as const;

/** Mirrors `.row` — avatar, email + chip, a short meta cell. */
function PersonRowSkeleton(): JSX.Element {
  return (
    <li className={s.row}>
      <Skeleton variant="circle" width={32} height={32} />
      <div className={s.rowMain}>
        <Skeleton width="55%" height={14} />
        <Skeleton width={56} height={14} radius="xl" />
      </div>
      <Skeleton width={48} height={12} className={s.rowMeta ?? ''} />
    </li>
  );
}

function PersonListSkeleton({ label }: { label: string }): JSX.Element {
  return (
    <ul className={s.list} role="status" aria-label={label}>
      {SKELETON_ROWS.map((row) => (
        <PersonRowSkeleton key={row} />
      ))}
    </ul>
  );
}

export function AdminInstituteDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const instituteId = id ?? '';

  const instituteQuery = useQuery({
    queryKey: ADMIN_QUERY_KEYS.institute(instituteId),
    queryFn: () => getInstitute(instituteId),
    enabled: instituteId !== '',
  });

  if (instituteId === '') {
    return <p className={s.error}>Missing institute id.</p>;
  }

  return (
    <div className={s.root}>
      <header className={s.header}>
        <Link to="/admin/institutes" className={s.crumb}>
          <span aria-hidden="true">←</span> All institutes
        </Link>
        {instituteQuery.isPending ? (
          <div className={s.titleSkeleton} role="status" aria-label="Loading institute">
            <Skeleton width="60%" height={36} />
            <Skeleton width="40%" height={16} />
          </div>
        ) : instituteQuery.error !== null ? (
          <StatusBanner tone="error">
            Could not load institute: {instituteQuery.error.message}
          </StatusBanner>
        ) : (
          <>
            <h1 className={s.title}>{instituteQuery.data?.name}</h1>
            <div className={s.meta}>
              <code>{instituteQuery.data?.slug}</code>
              <span>· owner {instituteQuery.data?.owner_email}</span>
              <span className={s.statusPill}>{instituteQuery.data?.status}</span>
            </div>
          </>
        )}
      </header>

      <MembersSection instituteId={instituteId} />
      <StudentsSection instituteId={instituteId} />
    </div>
  );
}

function MembersSection({ instituteId }: { instituteId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ADMIN_QUERY_KEYS.instituteMembers(instituteId),
    queryFn: () => listInstituteMembers(instituteId),
  });

  const mutate = useMutation({
    mutationFn: async (values: AddMemberInput) => {
      let provisioned = false;
      if (values.password !== '') {
        await provisionUser({ email: values.email, password: values.password });
        provisioned = true;
      }
      const row = await addMember({
        institute_id: instituteId,
        email: values.email,
        role: values.role,
      });
      return { row, provisioned };
    },
    onSuccess: ({ row, provisioned }) => {
      toast.show(
        provisioned
          ? `Added ${row.email} as ${row.role} — they can log in with the password you set.`
          : `Added ${row.email} as ${row.role}.`,
        { tone: 'success' },
      );
      tapHaptic();
      reset();
      void queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.instituteMembers(instituteId),
      });
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddMemberInput>({
    resolver: zodResolver(addMemberSchema),
    defaultValues: { email: '', role: 'teacher', password: '' },
  });

  async function onSubmit(values: AddMemberInput): Promise<void> {
    setServerError(null);
    try {
      await mutate.mutateAsync(values);
    } catch {
      /* onError set the serverError message */
    }
  }

  return (
    <section className={s.section}>
      <div className={s.sectionHead}>
        <span className={s.sectionIcon} aria-hidden="true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2 13.5v-1a2.5 2.5 0 0 1 2.5-2.5h5A2.5 2.5 0 0 1 12 12.5v1" />
            <circle cx="7" cy="5" r="2.5" />
            <path d="M13.5 8h1.5M14.25 7.25v1.5" />
          </svg>
        </span>
        <div>
          <h2 className={s.sectionTitle}>Members</h2>
          <p className={s.sectionSub}>
            Staff who manage the institute. They land on <code>/institute/dashboard</code> after
            login.
          </p>
        </div>
      </div>

      {list.isPending ? (
        <PersonListSkeleton label="Loading members" />
      ) : list.error !== null ? (
        <StatusBanner tone="error">{list.error.message}</StatusBanner>
      ) : (list.data ?? []).length === 0 ? (
        <p className={s.empty}>No members yet.</p>
      ) : (
        <ul className={s.list}>
          {list.data?.map((m) => (
            <li key={m.id} className={s.row}>
              <span className={s.avatar} aria-hidden="true">
                {initialsFromEmail(m.email)}
              </span>
              <div className={s.rowMain}>
                <span className={s.rowName}>{m.email}</span>
                <span className={s.rowChip}>{m.role}</span>
              </div>
              <span className={s.rowMeta}>{m.status}</span>
            </li>
          ))}
        </ul>
      )}

      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        className={s.memberForm}
      >
        <div>
          <Label htmlFor="member_email">Email</Label>
          <Input
            id="member_email"
            type="email"
            inputMode="email"
            autoComplete="off"
            placeholder="teacher@institute.com"
            invalid={errors.email !== undefined}
            aria-describedby={errors.email ? 'member_email-error' : undefined}
            {...register('email')}
          />
          <FormError id="member_email-error" message={errors.email?.message} />
        </div>
        <div>
          <Label htmlFor="member_role">Role</Label>
          <Select id="member_role" {...register('role')}>
            {INSTITUTE_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </div>
        <Button type="submit" className={s.formSubmit} loading={isSubmitting || mutate.isPending}>
          Add member
        </Button>
        <div className={s.memberFormFull}>
          <Label htmlFor="member_password">Password (optional)</Label>
          <Input
            id="member_password"
            type="password"
            autoComplete="new-password"
            placeholder="Min 8 chars. Leave blank if user has already signed up."
            invalid={errors.password !== undefined}
            aria-describedby={errors.password ? 'member_password-error' : undefined}
            {...register('password')}
          />
          <FormError id="member_password-error" message={errors.password?.message} />
        </div>

        {serverError !== null ? (
          <div className={s.memberFormFull}>
            <StatusBanner tone="error">{serverError}</StatusBanner>
          </div>
        ) : null}
      </form>
    </section>
  );
}

function StudentsSection({ instituteId }: { instituteId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ADMIN_QUERY_KEYS.instituteStudents(instituteId),
    queryFn: () => listInstituteStudents(instituteId),
  });

  const mutate = useMutation({
    mutationFn: async (values: AddStudentInput) => {
      let provisioned = false;
      if (values.password !== '') {
        await provisionUser({ email: values.email, password: values.password });
        provisioned = true;
      }
      const row = await addStudent({
        institute_id: instituteId,
        email: values.email,
        exam_type: values.exam_type,
        roll_no: values.roll_no === '' ? null : values.roll_no,
        target_band: values.target_band === '' ? null : parseFloat(values.target_band),
      });
      return { row, provisioned };
    },
    onSuccess: ({ row, provisioned }) => {
      toast.show(
        provisioned
          ? `Added ${row.email} as student — they can log in with the password you set.`
          : `Added ${row.email} as student.`,
        { tone: 'success' },
      );
      tapHaptic();
      reset();
      void queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.instituteStudents(instituteId),
      });
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddStudentInput>({
    resolver: zodResolver(addStudentSchema),
    defaultValues: {
      email: '',
      exam_type: 'academic',
      roll_no: '',
      target_band: '',
      password: '',
    },
  });

  // eslint-disable-next-line sonarjs/no-identical-functions -- different mutate.mutateAsync signature; sharing would require a generic helper that's not worth the abstraction
  async function onSubmit(values: AddStudentInput): Promise<void> {
    setServerError(null);
    try {
      await mutate.mutateAsync(values);
    } catch {
      /* onError set the serverError message */
    }
  }

  return (
    <section className={s.section}>
      <div className={s.sectionHead}>
        <span className={s.sectionIcon} aria-hidden="true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M1 7.5 8 4l7 3.5L8 11 1 7.5z" />
            <path d="M4 9v3.5c0 0 1.5 1.5 4 1.5s4-1.5 4-1.5V9" />
          </svg>
        </span>
        <div>
          <h2 className={s.sectionTitle}>Students</h2>
          <p className={s.sectionSub}>
            Students assigned to this institute. They land on <code>/student/home</code> after
            login.
          </p>
        </div>
      </div>

      {list.isPending ? (
        <PersonListSkeleton label="Loading students" />
      ) : list.error !== null ? (
        <StatusBanner tone="error">{list.error.message}</StatusBanner>
      ) : (list.data ?? []).length === 0 ? (
        <p className={s.empty}>No students yet.</p>
      ) : (
        <ul className={s.list}>
          {list.data?.map((st) => (
            <li key={st.id} className={s.row}>
              <span className={s.avatar} aria-hidden="true">
                {initialsFromEmail(st.email)}
              </span>
              <div className={s.rowMain}>
                <span className={s.rowName}>{st.email}</span>
                <span className={s.rowChip}>
                  {st.exam_type}
                  {st.roll_no === null ? '' : ` · roll ${st.roll_no}`}
                </span>
              </div>
              <span className={s.rowMeta}>
                {st.target_band === null ? '' : `target ${st.target_band}`}
              </span>
            </li>
          ))}
        </ul>
      )}

      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        className={s.studentForm}
      >
        <div>
          <Label htmlFor="student_email">Email</Label>
          <Input
            id="student_email"
            type="email"
            inputMode="email"
            autoComplete="off"
            placeholder="student@institute.com"
            invalid={errors.email !== undefined}
            aria-describedby={errors.email ? 'student_email-error' : undefined}
            {...register('email')}
          />
          <FormError id="student_email-error" message={errors.email?.message} />
        </div>
        <div>
          <Label htmlFor="student_exam_type">Exam type</Label>
          <Select id="student_exam_type" {...register('exam_type')}>
            {EXAM_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="student_roll">Roll # (opt)</Label>
          <Input id="student_roll" autoComplete="off" {...register('roll_no')} />
        </div>
        <div>
          <Label htmlFor="student_target">Target band</Label>
          <Input
            id="student_target"
            inputMode="decimal"
            placeholder="7.5"
            invalid={errors.target_band !== undefined}
            aria-describedby={errors.target_band ? 'student_target-error' : undefined}
            {...register('target_band')}
          />
          <FormError id="student_target-error" message={errors.target_band?.message} />
        </div>
        <Button type="submit" className={s.formSubmit} loading={isSubmitting || mutate.isPending}>
          Add student
        </Button>
        <div className={s.studentFormFull}>
          <Label htmlFor="student_password">Password (optional)</Label>
          <Input
            id="student_password"
            type="password"
            autoComplete="new-password"
            placeholder="Min 8 chars. Leave blank if student has already signed up."
            invalid={errors.password !== undefined}
            aria-describedby={errors.password ? 'student_password-error' : undefined}
            {...register('password')}
          />
          <FormError id="student_password-error" message={errors.password?.message} />
        </div>

        {serverError !== null ? (
          <div className={s.studentFormFull}>
            <StatusBanner tone="error">{serverError}</StatusBanner>
          </div>
        ) : null}
      </form>
    </section>
  );
}
